import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { useAuthStore } from '../../lib/auth';
import ProgressBar from '../../components/onboarding/ProgressBar';
import WelcomeStep from './WelcomeStep';
import WebsiteCreationStep from './WebsiteCreationStep';
import CompanyInfoStep from './CompanyInfoStep';
import ContentSourcesStep from './ContentSourcesStep';
import PlatformsStep from './PlatformsStep';
import ProcessingStep from './ProcessingStep';
import apiClient from '../../lib/api';

export default function OnboardingFlow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentStep } = useOnboardingStore();
  const { workspace, isAuthenticated, setWorkspace } = useAuthStore();
  const [isSkipping, setIsSkipping] = useState(false);

  // Redirect to dashboard if onboarding is already completed for this workspace
  // Redirect to login if not authenticated or no workspace
  useEffect(() => {
    if (!isAuthenticated || !workspace) {
      console.log('OnboardingFlow: No auth or workspace, redirecting to login');
      navigate('/login');
      return;
    }

    if (workspace.onboardingCompleted) {
      console.log('OnboardingFlow: Onboarding already completed, redirecting to dashboard');
      if (workspace.slug) {
        navigate(`/${workspace.slug}/dashboard`);
      } else {
        navigate('/login');
      }
    }
  }, [workspace, isAuthenticated, navigate]);

  // Skip onboarding handler
  const handleSkipOnboarding = async () => {
    if (!workspace?.id) return;

    setIsSkipping(true);
    try {
      await apiClient.patch(`/workspace/${workspace.id}`, {
        onboardingCompleted: true,
      });

      // Update local workspace state
      if (workspace) {
        setWorkspace({ ...workspace, onboardingCompleted: true });
      }

      // Navigate to dashboard
      if (workspace.slug) {
        navigate(`/${workspace.slug}/dashboard`);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
      setIsSkipping(false);
    }
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep />;
      case 2:
        return <WebsiteCreationStep />;
      case 3:
        return <CompanyInfoStep />;
      case 4:
        return <ContentSourcesStep />;
      case 5:
        return <PlatformsStep />;
      case 6:
        return <ProcessingStep />;
      default:
        return <WelcomeStep />;
    }
  };

  // Step 6 has its own full-screen layout
  if (currentStep === 6) {
    return <ProcessingStep />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Skip Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={handleSkipOnboarding}
            disabled={isSkipping}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSkipping ? t('onboarding.skipping') : t('onboarding.skipOnboarding')}
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <ProgressBar currentStep={currentStep} totalSteps={6} />
        </div>

        {/* Current Step Content */}
        <div className="animate-fadeIn">{renderStep()}</div>
      </div>
    </div>
  );
}
